// Purchase & credit history (spec §8): the full cross-agent ledger,
// paginated. Sign comes straight off amountCreditsNc — never derived from
// entryType. agentId NULL = platform-scope credit → "Platform".

import { useEffect } from 'react';
import { useBilling } from '@/src/hooks/useBilling';
import { useVaultSession } from '@/src/state/session';
import { useInViewport } from '@/src/lib/useInViewport';
import { formatCredits, formatSignedCredits } from '@/src/lib/credits';
import type { LedgerEntry } from '@/src/marketplace/payments';

const LABELS: Record<string, string> = {
  subscription_grant: 'Subscription',
  top_up: 'Top-up',
  platform_grant: 'Promotional',
  era_debit: 'Usage',
  expiration_burn: 'Expired',
};

function Row({ e, agentName }: { e: LedgerEntry; agentName: string }) {
  return (
    <tr>
      <td>{new Date(e.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
      <td>{agentName}</td>
      <td>{LABELS[e.entryType] ?? e.entryType}</td>
      <td>{formatSignedCredits(e.amountCreditsNc)}</td>
      <td>{formatCredits(e.runningBalanceNc)}</td>
    </tr>
  );
}

export function BillingHistory() {
  const { status, ledger } = useBilling();
  const { agentCards } = useVaultSession();
  // Billing now lives in the one-scroll home rather than its own route, so
  // this would otherwise auto-fetch on every home load. Gate on the section
  // actually being scrolled into view (fetch once, on first visibility).
  const [ref, inView] = useInViewport<HTMLElement>();

  useEffect(() => {
    if (inView && status === 'ready' && ledger.state === 'idle') ledger.loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, status, ledger.state, ledger.loadMore]);

  if (status !== 'ready') return null;

  const hasMore = ledger.total !== null && ledger.entries.length < ledger.total;

  return (
    <section ref={ref} aria-labelledby="billing-history">
      <h2 id="billing-history">Purchase & credit history</h2>
      {ledger.state === 'error' ? (
        <p>
          History unavailable.{' '}
          <button type="button" onClick={ledger.loadMore}>Retry</button>
        </p>
      ) : ledger.state === 'loading' && ledger.entries.length === 0 ? (
        <p>Loading history…</p>
      ) : ledger.state === 'ready' && ledger.entries.length === 0 ? (
        <p>No purchases yet.</p>
      ) : (
        <>
          <table className="vault-billing__history">
            <thead>
              <tr><th>Date</th><th>Agent</th><th>Type</th><th>Amount</th><th>Balance</th></tr>
            </thead>
            <tbody>
              {ledger.entries.map((e) => (
                <Row
                  key={e.id}
                  e={e}
                  // agentId null = platform-scope entry (no agent involved).
                  // Otherwise: card name if the agent is known in this
                  // session, else the raw id (Task 8's fallback pattern —
                  // displayAgentName shortens an id string, it doesn't take
                  // a card, so it doesn't apply here).
                  agentName={e.agentId === null ? 'Platform' : (agentCards[e.agentId]?.name ?? e.agentId)}
                />
              ))}
            </tbody>
          </table>
          {hasMore && <button type="button" onClick={ledger.loadMore}>Show more</button>}
        </>
      )}
    </section>
  );
}
